/* import __COLOCATED_TEMPLATE__ from './parameters.hbs'; */
/* eslint-disable @intercom/intercom/no-bare-strings */
/* RESPONSIBLE TEAM: team-data-foundations */
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import type IntlService from 'embercom/services/intl';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import type ParamDescriptor from 'embercom/models/workflow-connector/param-descriptor';
import {
  ATTRIBUTE_DESCRIPTOR_TYPE_STRING,
  ATTRIBUTE_DESCRIPTOR_TYPE_INTEGER,
  ATTRIBUTE_DESCRIPTOR_TYPE_DECIMAL,
  ATTRIBUTE_DESCRIPTOR_TYPE_BOOLEAN,
} from 'embercom/models/custom-objects/constants/attribute-descriptor-types';
import {
  PARAM_DESCRIPTOR_SOURCE_FIN,
  PARAM_DESCRIPTOR_SOURCE_CUSTOM,
  PARAM_DESCRIPTOR_SOURCE_ATTRIBUTE,
  PARAM_DESCRIPTOR_SOURCE_IGNORE,
} from 'embercom/models/workflow-connector/constants/parameter-descriptor-sources';

interface Args {
  usage: string;
  paramDescriptors: ParamDescriptor[];
  isMcpAction?: boolean;
  updateParamDescriptor: (
    index: string | undefined,
    name: string,
    description: string,
    type: string,
    required: boolean,
    defaultValue: string,
  ) => void;
  deleteParamDescriptor: (name: string) => void;
  usedAttributes: string[];
  updateBodyArguments?: () => void;
}

interface Signature {
  Element: HTMLElement;
  Args: Args;
}

export default class Parameters extends Component<Signature> {
  @service declare intl: IntlService;
  @service declare attributeService: any;
  @service declare intercomEventService: any;

  @tracked showModal = false;
  @tracked selectedFormat = 'string';
  @tracked index: string | undefined;
  @tracked name: string | undefined;
  @tracked description: string | undefined;
  @tracked selectedSource = 'fin';
  @tracked customValue: string | undefined;
  @tracked selectedAttributeIdentifier: string | undefined;

  getCompatibleTypes(paramType: string): string[] {
    switch (paramType) {
      case 'string':
        return [ATTRIBUTE_DESCRIPTOR_TYPE_STRING];
      case 'integer':
        return [ATTRIBUTE_DESCRIPTOR_TYPE_INTEGER, ATTRIBUTE_DESCRIPTOR_TYPE_DECIMAL];
      case 'boolean':
        return [ATTRIBUTE_DESCRIPTOR_TYPE_BOOLEAN];
      default:
        return [paramType];
    }
  }

  private isIgnored(source: string): boolean {
    return source === PARAM_DESCRIPTOR_SOURCE_IGNORE;
  }

  @action
  getFilteredAttributeGroupList(paramDescriptor: any) {
    if (!paramDescriptor || !paramDescriptor.type) {
      return [];
    }

    let paramType = paramDescriptor.type;
    let compatibleTypes = this.getCompatibleTypes(paramType);
    let allAttributeGroups = this.attributeService.attributeGroupListForUsers;

    // Conversation attributes (custom-action scope)
    let conversationAttributes = this.attributeService.conversationAttributesForCustomAction;
    if (conversationAttributes && conversationAttributes.length > 0) {
      allAttributeGroups = allAttributeGroups.concat([
        { heading: 'Conversation attributes', attributes: conversationAttributes },
      ]);
    }

    // Ticket attributes (flat list)
    let ticketAttributes = this.attributeService.ticketAttributesForCustomAction;
    if (ticketAttributes && ticketAttributes.length > 0) {
      allAttributeGroups = allAttributeGroups.concat([
        { heading: 'Ticket attributes', attributes: ticketAttributes },
      ]);
    }

    // Ticket attributes grouped by ticket type
    let ticketTypeGroups = this.attributeService.ticketAttributesByType;
    if (ticketTypeGroups && ticketTypeGroups.length > 0) {
      allAttributeGroups = allAttributeGroups.concat(ticketTypeGroups);
    }

    // Custom object attributes (grouped by object)
    let customObjectGroups = this.attributeService.customObjectAttributeGroups;
    if (customObjectGroups && customObjectGroups.length > 0) {
      allAttributeGroups = allAttributeGroups.concat(customObjectGroups);
    }

    return allAttributeGroups
      .map((group: any) => {
        if (!group.attributes) {
          return group;
        }

        let filteredAttributes = group.attributes.filter((attribute: any) => {
          return compatibleTypes.includes(attribute.type);
        });

        return {
          ...group,
          attributes: filteredAttributes,
        };
      })
      .filter((group: any) => group.attributes && group.attributes.length > 0);
  }

  get columns() {
    return [
      {
        label: this.intl.t('workflow-connector.builder.body.request.fin-data.table.name'),
        valuePath: 'name',
        isSortable: true,
      },
      {
        label: this.intl.t('workflow-connector.builder.body.request.fin-data.table.format'),
        valuePath: 'type',
        isSortable: true,
      },
      {
        label: this.intl.t('workflow-connector.builder.body.request.fin-data.table.description'),
        valuePath: 'description',
        isSortable: true,
      },
      {
        label: '',
        valuePath: 'actions',
      },
    ];
  }

  get formatOptions() {
    return [
      {
        value: 'string',
        text: this.intl.t(
          'workflow-connector.builder.body.request.fin-data.modal.form.format-types.string',
        ),
        icon: 'field',
      },
      {
        value: 'integer',
        text: this.intl.t(
          'workflow-connector.builder.body.request.fin-data.modal.form.format-types.integer',
        ),
        icon: 'numbers',
      },
      {
        value: 'decimal',
        text: this.intl.t(
          'workflow-connector.builder.body.request.fin-data.modal.form.format-types.decimal',
        ),
        icon: 'decimal',
      },
      {
        value: 'boolean',
        text: this.intl.t(
          'workflow-connector.builder.body.request.fin-data.modal.form.format-types.boolean',
        ),
        icon: 'boolean',
      },
    ];
  }

  get paramDescriptorSourceOptions() {
    return [
      {
        text: this.intl.t('workflow-connector.actions.parameter-descriptor-sources.fin'),
        value: PARAM_DESCRIPTOR_SOURCE_FIN,
        icon: 'fin',
      },
      {
        text: this.intl.t('workflow-connector.actions.parameter-descriptor-sources.custom'),
        value: PARAM_DESCRIPTOR_SOURCE_CUSTOM,
        icon: 'draft',
      },
      {
        text: this.intl.t('workflow-connector.actions.parameter-descriptor-sources.attribute'),
        value: PARAM_DESCRIPTOR_SOURCE_ATTRIBUTE,
        icon: 'attribute-titles',
      },
    ];
  }

  get paramDescriptorSourceOptionsWithIgnore() {
    return [
      ...this.paramDescriptorSourceOptions,
      {
        text: this.intl.t('workflow-connector.actions.parameter-descriptor-sources.ignore'),
        icon: 'unfollow',
        value: PARAM_DESCRIPTOR_SOURCE_IGNORE,
      },
    ];
  }

  @action
  getDataSourceOptionsForParam(paramDescriptor: any) {
    return paramDescriptor.required
      ? this.paramDescriptorSourceOptions
      : this.paramDescriptorSourceOptionsWithIgnore;
  }

  get canSave() {
    return this.name && this.description;
  }

  clearModal() {
    this.index = undefined;
    this.selectedFormat = 'string';
    this.name = undefined;
    this.description = undefined;
  }

  @action
  editParamDescriptor(data: any) {
    this.index = data.name;
    this.name = data.name;
    this.description = data.description;
    this.selectedFormat = data.type;
    this.showModal = true;
  }

  @action
  onSelectParamAttribute(paramDescriptor: any, attribute: any) {
    if (this.args.isMcpAction && attribute) {
      this.trackAction('mcp_parameter_attribute_selected', paramDescriptor.source);
    }

    if (attribute) {
      paramDescriptor.set('value', attribute.identifier);
    } else {
      paramDescriptor.set('value', undefined);
    }

    if (this.args.updateBodyArguments) {
      this.args.updateBodyArguments();
    }
  }

  @action
  onParamSourceChange(paramDescriptor: any, newSource: string) {
    if (this.args.isMcpAction) {
      this.trackAction('mcp_parameter_configured', newSource);
    }

    this.updateParameterEnabledState(paramDescriptor, newSource);
    this.updateParameterSource(paramDescriptor, newSource);
    this.updateParameterValue(paramDescriptor, newSource);
    this.notifyBodyArgumentsUpdate();
  }

  trackAction(actionName: string, parameterSource: string) {
    this.intercomEventService.trackAnalyticsEvent({
      action: actionName,
      object: 'mcp_parameter',
      parameter_source: parameterSource,
    });
  }

  private updateParameterEnabledState(paramDescriptor: any, newSource: string) {
    paramDescriptor.set('enabled', !this.isIgnored(newSource));
  }

  private updateParameterSource(paramDescriptor: any, newSource: string) {
    this.isIgnored(newSource)
      ? paramDescriptor.set('source', null)
      : paramDescriptor.set('source', newSource);
  }

  private updateParameterValue(paramDescriptor: any, newSource: string) {
    let defaultValue = this.getDefaultValueForSource(paramDescriptor, newSource);
    paramDescriptor.set('value', defaultValue);
  }

  private getDefaultValueForSource(paramDescriptor: any, newSource: string): any {
    // FIN and IGNORE sources don't require user-provided values
    if (newSource === PARAM_DESCRIPTOR_SOURCE_FIN || this.isIgnored(newSource)) {
      return null;
    }

    if (newSource === PARAM_DESCRIPTOR_SOURCE_CUSTOM) {
      return paramDescriptor.type === 'boolean' ? false : null;
    }

    // Attribute sources start with no selection when switching from other sources
    if (newSource === PARAM_DESCRIPTOR_SOURCE_ATTRIBUTE) {
      let oldSource = paramDescriptor.source;
      return oldSource !== PARAM_DESCRIPTOR_SOURCE_ATTRIBUTE ? null : paramDescriptor.value;
    }

    return null;
  }

  private notifyBodyArgumentsUpdate() {
    if (this.args.updateBodyArguments) {
      this.args.updateBodyArguments();
    }
  }

  @action
  onCustomValueChange(paramDescriptor: any, event: Event) {
    let target = event.target as HTMLInputElement;
    paramDescriptor.set('value', target.value);

    if (this.args.updateBodyArguments) {
      this.args.updateBodyArguments();
    }
  }

  @action
  onCustomBooleanChange(paramDescriptor: any) {
    let currentValue = paramDescriptor.value;
    let newValue = !currentValue;

    paramDescriptor.set('value', newValue);

    if (this.args.updateBodyArguments) {
      this.args.updateBodyArguments();
    }
  }

  @action
  getSelectedSource(paramDescriptor: any) {
    if (paramDescriptor.enabled === false) {
      let availableOptions = this.getDataSourceOptionsForParam(paramDescriptor).map(
        (option) => option.value,
      );
      return availableOptions.includes(PARAM_DESCRIPTOR_SOURCE_IGNORE)
        ? PARAM_DESCRIPTOR_SOURCE_IGNORE
        : PARAM_DESCRIPTOR_SOURCE_FIN;
    }

    return paramDescriptor.source || PARAM_DESCRIPTOR_SOURCE_FIN;
  }

  @action
  shouldShowCustomInput(paramDescriptor: any) {
    return (
      paramDescriptor.enabled !== false && paramDescriptor.source === PARAM_DESCRIPTOR_SOURCE_CUSTOM
    );
  }

  @action
  shouldShowAttributeSelect(paramDescriptor: any) {
    return (
      paramDescriptor.enabled !== false &&
      paramDescriptor.source === PARAM_DESCRIPTOR_SOURCE_ATTRIBUTE
    );
  }

  @action
  deleteParamDescriptor(data: any) {
    this.args.deleteParamDescriptor(data.name);
  }

  @action
  onFormatChange(format: string) {
    this.selectedFormat = format;
  }

  @action
  onCancel() {
    this.clearModal();
    this.showModal = false;
  }

  @action
  onSave() {
    if (this.name && this.description) {
      this.args.updateParamDescriptor(
        this.index,
        this.name,
        this.description,
        this.selectedFormat,
        false,
        '',
      );
      this.showModal = false;
      this.clearModal();
    }
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'WorkflowConnector::Builder::Body::Sections::Request::Parameters': typeof Parameters;
    'workflow-connector/builder/body/sections/request/parameters': typeof Parameters;
  }
}
